<template>
  <div
    class="dropdown"
    ref="dropdown"
    :style="dropdownStyle()"
    @mousedown="handleMousedown"
    @mouseover="handleMouseover"
  >
    <slot />
  </div>
</template>
<script setup lang="ts">
import {
  defineEmits,
  shallowRef,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  VueElement,
} from "vue";
import { dropdownProps, dropdownEmit } from "./select-dropdown";
import { withDebounce } from "@/composables/use-debounce";

const MAX_HEIGHT = 300;
const MIN_HEIGHT = 100;
// 暂时先写死，后续通过获取子OPTION的高度更新
let SINGLE_OPTION_HEIGHT = 38;

const props = defineProps(dropdownProps);
const emits = defineEmits(dropdownEmit);

const dropdown = shallowRef<VueElement>();
const _ref = computed(() => dropdown.value);

const nativeSelectValue = computed(() => String(props.selectValue));
const observer = new MutationObserver(
  withDebounce(() => {
    // 对于异步接口，重算MIN&MAX HEIGHT
    const height = calcHeight(_ref.value?.children.length || 99);
    if (_ref.value?.style.minHeight !== undefined) {
      _ref.value.style.minHeight = height.minHeight;
    }
    if (_ref.value?.style.maxHeight !== undefined) {
      _ref.value.style.maxHeight = height.maxHeight;
    }

    emits("optionUpdate");
  })
);

const dropdownStyle = () => {
  let style = {};
  // dropdown宽度
  if (props.dropWidth) {
    style = Object.assign(style, { width: props.dropWidth + "px" });
  } else if (props.width) {
    style = Object.assign(style, { width: props.width + "px" });
  } else {
    style = Object.assign(style, { width: "100%" });
  }

  // 通过子option的个数计算高度，并且重新写入min-height和max-height
  if (_ref.value?.children && _ref.value.children.length) {
    style = Object.assign(style, calcHeight(_ref.value.children.length));
  }

  // 背景颜色
  if (props.dropBackground) {
    style = Object.assign(style, { backgroundColor: props.dropBackground });
  }

  if (props.dropTop) {
    style = Object.assign(style, { top: 56 - (50 - props.dropTop) + "px" });
  }

  return style;
};

const calcHeight = (len: number) => {
  const result = {
    minHeight: MIN_HEIGHT,
    maxHeight: MAX_HEIGHT,
  };
  // 计算子元素的高度，如果option内部没有高度值按默认计算（暂时没有这个情况，待优化）
  if (_ref.value?.children.length && _ref.value?.children[0].children.length) {
    // 20为option的默认padding，这边是为了计算单个option的高度
    let height = 0;
    for (let i = 0; i < _ref.value?.children[0].children.length; i++) {
      height = height + _ref.value?.children[0].children[i].clientHeight;
    }
    SINGLE_OPTION_HEIGHT = 20 + (height || 18);
  }
  if (len * SINGLE_OPTION_HEIGHT < MAX_HEIGHT) {
    result.maxHeight = len * SINGLE_OPTION_HEIGHT;
  }
  if (len * SINGLE_OPTION_HEIGHT < MIN_HEIGHT) {
    result.minHeight = len * SINGLE_OPTION_HEIGHT;
  }

  return { minHeight: result.minHeight + "px", maxHeight: result.maxHeight + "px" };
};

const handleMousedown = (e: Event) => {
  // disabled情况下，移除事件触发
  const target = e.target as HTMLLIElement;
  if (target?.className === 'dropdown' && target?.nodeName === 'DIV') {
    return;
  }
  if (target.getAttribute("class")?.includes("disabled")) {
    e.preventDefault();
    return;
  }
  // 如果获取不到value，就需要获取其Parent的Node。递归向上
  const element = getTargetValue(target);
  emits("optionClick", { value: element._value, label: element.innerText });
};

const getTargetValue = (target: any) => {
  let result: any = target;
  if (target._value) {
    return target;
  }
  if (target.parentElement) {
    result = getTargetValue(target.parentElement);
  }
  return result;
};

const handleMouseover = () => {
  if (activeElement.value) {
    activeElement.value.setAttribute("class", "option");
  }
};

// output 方法集合
const activeElement = ref();
const setOptionFontColor = () => {
  const { children } = _ref.value as any;
  for (let c of children) {
    if (c._value === nativeSelectValue.value) {
      c.setAttribute("class", "option option-active");
      activeElement.value = c;
    }
  }
};

const removeOptionFontColor = () => {
  const { children } = _ref.value as any;
  for (let c of children) {
    if (c._value === nativeSelectValue.value) {
      c.setAttribute("class", "option");
    }
  }
};

const getOptionLabel = (v: string) => {
  const { children } = _ref.value as any;
  for (let c of children) {
    if (c._value === v) {
      return c.innerText || "";
    }
  }
};

defineExpose({
  getOptionLabel,
  setOptionFontColor,
  removeOptionFontColor,
});

onMounted(() => {
  observer.observe(_ref.value!, {
    childList: true,
    subtree: true,
  });
});

onBeforeUnmount(() => {
  observer.disconnect();
});
</script>
<style lang="less" scoped>
.dropdown {
  min-height: 100px;
  max-height: 300px;
  background-color: #fff;
  position: absolute;
  top: 56px;
  left: 0px;
  z-index: 100;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.12);
  border: 1px solid var(--vt-c-white-soft);
  overflow-y: auto;
}
</style>
